<template>
  <div class="controls">
    <div class="tags-top">
      <div class="tags">
        <template v-if="filters">
          <Lock
            v-for="(row, index) in filters"
            :key="index"
            :permission="filterPermission(row)"
          >
            <div
              class="tag"
              :class="{ 'is-active': row.isActive }"
              @click="toggleFilter(row.key)"
            >
              {{ row.name }} <span class="count"> [{{ row.count }}] </span>
            </div>
          </Lock>
        </template>

        <Lock v-if="isChangesOnly" :permission="filterKey">
          <label class="tag" :class="{ 'is-active': changesOnly }">
            <input
              v-model="changesOnly"
              class="visually-hidden"
              type="checkbox"
            />
            Changes only</label
          >
        </Lock>
        <Search class="tag tag-search" :class="{ 'is-active': searchText }" />
      </div>
      <div v-if="sorting" class="tags tags-right">
        <Lock
          v-for="(sort, index) in sorting"
          :key="index"
          :permission="sortKey"
        >
          <span
            class="tag tag-icon"
            :class="{ 'is-active': sort.isActive }"
            @click="setSort(sort.key, sort.isDESC ? 'ASC' : 'DESC')"
          >
            <TriangleIcon
              v-if="
                (sort.isDESC && sort.isActive) ||
                !sort.isActive ||
                (sort.isASC && sort.isActive)
              "
              :rotate="sort.isASC && sort.isActive ? 'up' : 'down'"
            />
            <span>{{ sort.name }}</span>
          </span>
        </Lock>
      </div>
    </div>
    <div class="tags-bottom">
      <template v-if="signalFilters">
        <div class="tags">
          <Lock
            v-for="(row, index) in signalFilters"
            :key="index"
            :permission="signalKey"
          >
            <div
              class="tag"
              :class="{ 'is-active': row.isActive }"
              @click="toggleSignalFilter(row.key)"
            >
              {{ row.name }} <span class="count"> [{{ row.count }}] </span>
            </div>
          </Lock>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import TriangleIcon from "./icons/Triangle.vue";
import Search from "./Search.vue";
import Lock from "./Lock.vue";
import { MATCH_FILTER_KEYS } from "../../store/utils/constant";

const props = defineProps({
  filters: {
    type: Array,
    default: null,
  },
  signalFilters: {
    type: Array,
    default: null,
  },
  sorting: {
    type: Array,
    default: null,
  },
  constants: {
    type: Object,
    required: true,
  },
  isChangesOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits();

const store = useStore();

// A-9: секция берётся из имени роута (live/prematch/ticker) — надёжнее, чем
// constants.CATEGORY (у Live его нет, у Ticker он "match")
const route = useRoute();
const section = computed(() => String(route.name || "").toUpperCase());
const viewKey = computed(() => `${section.value}_VIEW`);
const sortKey = computed(() => `${section.value}_SORT`);
const filterKey = computed(() => `${section.value}_FILTER`);
const signalKey = computed(() => `${section.value}_SIGNAL`);
const favoriteKey = computed(() => `${section.value}_FAVORITE`);

// Каждая вкладка гейтится своим правом: ALL — по VIEW, Favorites — по FAVORITE,
// Signals — по SIGNAL, остальное (поиск и т.п.) — по FILTER
function filterPermission(row) {
  switch (row.key) {
    case MATCH_FILTER_KEYS.ALL:
      return viewKey.value;
    case MATCH_FILTER_KEYS.FAVORITES:
      return favoriteKey.value;
    case MATCH_FILTER_KEYS.SIGNALS:
      return signalKey.value;
    default:
      return filterKey.value;
  }
}

const page = ref(true);

const state = computed(() => store.state[props.constants.CATEGORY]);
const searchText = computed(() => store.state.match.searchText);

const changesOnly = computed({
  get() {
    const found = state.value.filters.find(
      (filter) =>
        typeof filter === "object" &&
        filter.type === MATCH_FILTER_KEYS.CHANGES_ONLY,
    );
    return found?.value !== false;
  },
  set(filterValue) {
    // A-9: гейтинг на контроле (<Lock>), здесь только действие
    store.commit("TOGLE_PREMATCH_SINGLE_FILTER", {
      filterType: props.constants.MATCH_FILTER_KEYS.CHANGES_ONLY,
      filterValue: filterValue ? companyId.value : false,
    });
  },
});

function setSort(type, order) {
  store.commit(
    props.constants.CATEGORY === "prematch" ? "SET_PREMATCH_SORT" : "SET_SORT",
    { type, order },
  );
}

function toggleFilter(filterKey) {
  store.commit(
    props.constants.CATEGORY === "prematch"
      ? "SET_PREMATCH_FILTER"
      : "SET_FILTER",
    filterKey,
  );
}

function toggleSignalFilter(filterValue) {
  store.commit("TOGGLE_TICKER_SIGNAL_FILTER", {
    filterKey: props.constants.MATCH_FILTER_KEYS.SIGNAL_TYPES,
    filterValue,
  });
}

const companyId = computed(() => {
  return state.value?.companyId ?? null;
});
</script>

<style lang="scss" scoped>
@use "../../scss/variables" as *;

.controls {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 1rem;

  @media (max-width: $tablet) {
    flex-direction: column;
    gap: 5px;
  }
}

.tags-bottom {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: $tablet) {
    flex-direction: column;
    gap: 5px;
  }
}

.tags-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;

  @media (max-width: $tablet) {
    gap: 5px;
  }
}

.tags-right {
  justify-content: end;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 10px;

  @media (max-width: $tablet) {
    gap: 5px;
  }
}

.tag {
  cursor: pointer;
  user-select: none;
  border: 1px solid var(--font-color);
  background-color: transparent;
  border-radius: 100px;
  color: var(--back-color);
  font-size: 12px;
  text-transform: uppercase;
  padding: 4px 10px;
  white-space: nowrap;

  .count {
    padding-left: 5px;
    color: var(--font-color);
  }
}
.tag-search {
  padding: 0 10px;
}

.is-active {
  background-color: var(--success-background-opacity-medium);
  font-weight: 500;
}

.tag-icon {
  display: flex;
  align-items: center;
  gap: 5px;
  fill: var(--font-color);

  svg {
    width: 10px;
  }
}
</style>
